import { useEffect, useRef, useState } from 'react';
import { clearOutbox } from '../lib/offline/outbox.js';
import { clearSavedQueries } from '../lib/offline/persist.js';
import { QRCodeSVG } from 'qrcode.react';
import { useQueryClient } from '@tanstack/react-query';
import { setUpTOTP, verifyTOTPSetup, updateMFAPreference, signOut } from '../lib/auth.js';
import { useAuth } from '../context/AuthContext.js';
import { theme } from '../theme.js';
import { Button } from '../components/Button.js';

/**
 * Shown instead of the app when the user's role requires two-factor auth
 * and none is enrolled. Once verified, Cognito asks for a code at every
 * sign-in and the API lets them through.
 */
export function MfaSetupPage() {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const [setupUri, setSetupUri] = useState<string>();
  const [secret, setSecret] = useState<string>();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Each setUpTOTP call issues a new secret and invalidates the previous one,
  // so run it once even under StrictMode's double effects.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    setUpTOTP()
      .then((details) => {
        setSecret(details.sharedSecret);
        setSetupUri(details.getSetupUri('MusterTap', user?.email).toString());
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not start setup'));
  }, [user?.email]);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyTOTPSetup({ code: code.trim() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      await qc.invalidateQueries({ queryKey: ['me'] });
      await qc.invalidateQueries({ queryKey: ['system', 'me'] });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That code didn’t work — try the next one');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    await Promise.all([clearOutbox(), clearSavedQueries()]); // nothing of theirs for the next sign-in
    qc.clear();
    await refresh();
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: theme.bg, padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, marginBottom: 8 }}>
          Set up two-factor authentication
        </div>
        <div style={{ fontSize: 14, color: theme.textDim, lineHeight: 1.5, marginBottom: 20 }}>
          Admin accounts need a second sign-in step. Scan this code with an authenticator
          app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the
          6-digit code it shows.
        </div>

        <div style={{
          background: '#fff', borderRadius: 12, padding: 16,
          display: 'flex', justifyContent: 'center', marginBottom: 12,
          border: `1px solid ${theme.border}`, minHeight: 212,
          alignItems: 'center',
        }}>
          {setupUri
            ? <QRCodeSVG value={setupUri} size={180} />
            : <span style={{ fontSize: 13, color: '#666' }}>Preparing…</span>}
        </div>

        {secret && (
          <details style={{ marginBottom: 20 }}>
            <summary style={{ fontSize: 13, color: theme.textMuted, cursor: 'pointer' }}>
              Can’t scan? Enter this key instead
            </summary>
            <code style={{
              display: 'block', marginTop: 8, padding: '8px 10px', borderRadius: 8,
              background: theme.surface2, color: theme.text, fontSize: 13,
              wordBreak: 'break-all', letterSpacing: 1,
            }}>
              {secret}
            </code>
          </details>
        )}

        <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
            aria-label="6-digit code" required
            style={{
              padding: '12px 14px', borderRadius: 10, fontSize: 20, letterSpacing: 6,
              textAlign: 'center', fontFamily: theme.font,
              border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text,
            }}
          />

          {error && (
            <div style={{
              fontSize: 13, color: theme.status.danger.fg, background: theme.status.danger.bg,
              padding: '10px 14px', borderRadius: 8,
            }}>{error}</div>
          )}

          <Button type="submit" fullWidth size="lg" loading={loading} disabled={!setupUri || code.length !== 6}>
            Verify and continue
          </Button>
        </form>

        <button
          onClick={handleSignOut}
          style={{
            marginTop: 16, width: '100%', background: 'none', border: 'none',
            color: theme.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: theme.font,
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
